import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import {
  UserApi,
  UsersFilter,
  ScopePayload,
  ExclusionPayload,
  CreateUserAndOtpResponse,
  UserScopesPagingResponse,
  UserScopeExclusionsPagingResponse,
  UserScopeRecord,
  UserScopeExclusionRecord,
  UserScopeUpdateBody,
  UserScopeExclusionUpdateBody,
  UserScopeType,
  BusinessUserMe,
} from '../models';
import { API_BUSINESS_URL } from '@/utils';
import { normalizeListedUser } from '../utils/normalizeUser';

export type GetUsersQueryArgs = {
  filters: UsersFilter;
  paginated: PaginatedRequest;
};

export type GetUserScopesPageArgs = {
  userId: string;
  limit: number;
  offset: number;
  scopeType?: UserScopeType;
  scopeId?: string;
};

export type GetUserScopeExclusionsPageArgs = {
  userId: string;
  limit: number;
  offset: number;
  scopeType?: UserScopeType;
  scopeId?: string;
};

const usersService = createApi({
  reducerPath: 'businessUsersService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/users`),
  tagTypes: ['Users'],
  keepUnusedDataFor: 30,
  refetchOnMountOrArgChange: 60,
  endpoints: (builder) => ({
    getUsers: builder.query<PaginatedResponse<UserApi>, GetUsersQueryArgs>({
      query: ({ paginated, filters }: GetUsersQueryArgs) => {
        const { search, phoneNumber, status, userType } = filters;

        const params: Record<string, string | number> = {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        };

        if (search && search.trim() !== '') {
          params.search = search.trim();
        }
        if (phoneNumber && phoneNumber.trim() !== '') {
          params.phoneNumber = phoneNumber.trim();
        }
        if (status) {
          params.status = status;
        }
        if (userType) {
          params.userType = userType;
        }

        return {
          url: '',
          params,
        };
      },
      transformResponse: (response: PaginatedResponse<UserApi>) => ({
        ...response,
        items: (response.items ?? []).map((user) => normalizeListedUser(user)),
      }),
      providesTags: ['Users'],
    }),

    getUserById: builder.query<UserApi, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
      }),
      transformResponse: (response: UserApi) => normalizeListedUser(response),
      providesTags: ['Users'],
    }),

    getBusinessUserMe: builder.query<BusinessUserMe, void>({
      query: () => ({
        url: '/whoami',
      }),
      providesTags: ['Users'],
    }),

    createUser: builder.mutation<UserApi, Partial<UserApi>>({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),

    updateUser: builder.mutation<
      UserApi,
      { id: string; body: Partial<UserApi> }
    >({
      query: ({ id, body }) => ({
        url: `/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Users'],
    }),

    activateUser: builder.mutation<UserApi, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}/activate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Users'],
    }),

    deactivateUser: builder.mutation<UserApi, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}/deactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Users'],
    }),

    deleteUser: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),

    createScope: builder.mutation<
      void,
      { userId: string; scope: ScopePayload }
    >({
      query: ({ userId, scope }) => ({
        url: `/${userId}/scopes`,
        method: 'POST',
        body: scope,
      }),
      invalidatesTags: ['Users'],
    }),

    createExclusion: builder.mutation<
      void,
      { userId: string; exclusion: ExclusionPayload }
    >({
      query: ({ userId, exclusion }) => ({
        url: `/${userId}/scopes/exclusions`,
        method: 'POST',
        body: exclusion,
      }),
      invalidatesTags: ['Users'],
    }),

    getUserScopesPage: builder.query<
      UserScopesPagingResponse,
      GetUserScopesPageArgs
    >({
      query: ({ userId, limit, offset, scopeType, scopeId }) => {
        const params: Record<string, string | number> = { limit, offset };
        if (scopeType) params.scopeType = scopeType;
        if (scopeId) params.scopeId = scopeId;
        return {
          url: `/${userId}/scopes`,
          params,
        };
      },
      transformResponse: (response: UserScopesPagingResponse) => ({
        ...response,
        items: (response.items ?? []).map((row) => ({ ...row })),
      }),
    }),

    getUserScopeExclusionsPage: builder.query<
      UserScopeExclusionsPagingResponse,
      GetUserScopeExclusionsPageArgs
    >({
      query: ({ userId, limit, offset, scopeType, scopeId }) => {
        const params: Record<string, string | number> = { limit, offset };
        if (scopeType) params.scopeType = scopeType;
        if (scopeId) params.scopeId = scopeId;
        return {
          url: `/${userId}/scopes/exclusions`,
          params,
        };
      },
      transformResponse: (response: UserScopeExclusionsPagingResponse) => ({
        ...response,
        items: (response.items ?? []).map((row) => ({ ...row })),
      }),
    }),

    updateUserScope: builder.mutation<
      UserScopeRecord,
      {
        userId: string;
        scopeRowId: string;
        body: UserScopeUpdateBody;
      }
    >({
      query: ({ userId, scopeRowId, body }) => ({
        url: `/${userId}/scopes/${scopeRowId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Users'],
    }),

    deleteUserScope: builder.mutation<
      void,
      { userId: string; scopeRowId: string }
    >({
      query: ({ userId, scopeRowId }) => ({
        url: `/${userId}/scopes/${scopeRowId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),

    updateUserScopeExclusion: builder.mutation<
      UserScopeExclusionRecord,
      {
        userId: string;
        exclusionRowId: string;
        body: UserScopeExclusionUpdateBody;
      }
    >({
      query: ({ userId, exclusionRowId, body }) => ({
        url: `/${userId}/scopes/exclusions/${exclusionRowId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Users'],
    }),

    deleteUserScopeExclusion: builder.mutation<
      void,
      { userId: string; exclusionRowId: string }
    >({
      query: ({ userId, exclusionRowId }) => ({
        url: `/${userId}/scopes/exclusions/${exclusionRowId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),

    createUserAndOtp: builder.mutation<
      CreateUserAndOtpResponse,
      {
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
        operators: {
          includeAll: boolean;
          selected: string[];
          subOperators: {
            includeAll: boolean;
            selected: string[];
            excluded: string[];
          };
          excluded: string[];
        };
        commerces: {
          includeAll: boolean;
          selected: string[];
          excluded: string[];
        };
        branches: {
          includeAll: boolean;
          selected: string[];
          excluded: string[];
        };
        phoneNumber?: string;
      }
    >({
      query: (body) => ({
        url: '/create-user-and-otp',
        method: 'POST',
        body,
      }),
      invalidatesTags: [],
    }),

    /**
     * Alta atómica: datos + roles + scopes + exclusiones en una sola llamada.
     * El backend rechaza la combinación comercio/operador antes de crear nada,
     * así que no puede quedar un usuario a medio configurar.
     */
    inviteUser: builder.mutation<
      CreateUserAndOtpResponse,
      {
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        roleIds: string[];
        scopes: ScopePayload[];
        exclusions: ExclusionPayload[];
      }
    >({
      query: (body) => ({
        url: '/invite',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),

    sendInvitation: builder.mutation<
      void,
      {
        userId: string;
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        invitationLink: string;
      }
    >({
      query: (body) => ({
        url: '/send-invitation',
        method: 'POST',
        body,
      }),
      invalidatesTags: [],
    }),

    sendOtpByEmail: builder.mutation<void, { userId: string }>({
      query: ({ userId }) => ({
        url: `${API_BUSINESS_URL}/user-otp/send-email/${userId}`,
        method: 'POST',
      }),
      invalidatesTags: [],
    }),
  }),
});

export default usersService;

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useGetBusinessUserMeQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useDeleteUserMutation,
  useCreateScopeMutation,
  useCreateExclusionMutation,
  useGetUserScopesPageQuery,
  useLazyGetUserScopesPageQuery,
  useGetUserScopeExclusionsPageQuery,
  useLazyGetUserScopeExclusionsPageQuery,
  useUpdateUserScopeMutation,
  useDeleteUserScopeMutation,
  useUpdateUserScopeExclusionMutation,
  useDeleteUserScopeExclusionMutation,
  useCreateUserAndOtpMutation,
  useInviteUserMutation,
  useSendInvitationMutation,
  useSendOtpByEmailMutation,
} = usersService;
